import React, { useState, useEffect, useCallback } from 'react'
import { wellService, checkSupabaseConfig, supabase } from '../supabase'
import ConstructionRecordForm from './ConstructionRecordForm'
import WellForm from './WellForm'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import UserManagement from './UserManagement'
import PvcPipesQuery from './DataAnalysis'
import MotorQuery from './MotorQuery'
import NewPage from './NewPage'
import WiringDiagram from './WiringDiagram'
import ConstructionRecordList from './ConstructionRecordList'
import './WellList.css'

const WellList = () => {
  const [wells, setWells] = useState([])
  const [filteredWells, setFilteredWells] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [configError, setConfigError] = useState(null)
  const [expandedCards, setExpandedCards] = useState({})

  // 導航相關狀態
  const [showFloatingMenu, setShowFloatingMenu] = useState(false)
  const [currentPage, setCurrentPage] = useState('wells')

  // 施工紀錄表單與列表狀態
  const [isConstructionFormOpen, setIsConstructionFormOpen] = useState(false)
  const [isConstructionListOpen, setIsConstructionListOpen] = useState(false)

  // 新增水井表單狀態
  const [isWellFormOpen, setIsWellFormOpen] = useState(false)
  const [editingWell, setEditingWell] = useState(null)

  // 登入相關狀態
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [userRole, setUserRole] = useState('viewer')
  const [isLoginFormOpen, setIsLoginFormOpen] = useState(false)
  const [isRegisterFormOpen, setIsRegisterFormOpen] = useState(false)
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false)

  // 搜索和篩選狀態
  const [searchTerm, setSearchTerm] = useState('')
  const [areaFilter, setAreaFilter] = useState('')
  const [wellTypeFilter, setWellTypeFilter] = useState('')
  const [motorTypeFilter, setMotorTypeFilter] = useState('')

  // 檢查登入狀態
  const checkLoginStatus = useCallback(async () => {
    console.log('開始檢查登入狀態...')

    try {
      if (!supabase) {
        console.log('Supabase未初始化')
        setIsLoggedIn(false)
        return
      }

      console.log('檢查Supabase會話...')
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        console.error('檢查登入狀態失敗:', error)
        clearLoginData()
        setIsLoggedIn(false)
        return
      }

      if (session && session.user) {
        console.log(`找到會話: ${session.user.email}`)

        try {
          console.log('開始檢查權限...')
          const { role, isActive } = await checkUserPermission(session.user.email)
          console.log(`權限檢查結果: ${role}, 啟用: ${isActive}`)

          // 檢查權限是否與localStorage中的不同
          const storedRole = localStorage.getItem('userRole')
          const roleChanged = storedRole && storedRole !== role

          if (roleChanged) {
            console.log(`權限已更新: ${storedRole} -> ${role}，立即生效`)
          }

          // 處理帳號狀態
          if (!isActive && role !== 'admin') {
            if (role === 'pending') {
              // 待審核帳號，給予查看權限
              setIsLoggedIn(true)
              setUserRole('viewer')
              localStorage.setItem('userRole', 'viewer')
            } else {
              // 帳號被停用，強制登出
              clearLoginData()
              setIsLoggedIn(false)
              setUserRole(null)
              alert('您的帳號已被管理員停用，請聯繫管理員重新啟用。')
            }
            return
          }

          if (role === 'pending') {
            // 帳號待審核，給予查看權限
            setIsLoggedIn(true)
            setUserRole('viewer')
            localStorage.setItem('userRole', 'viewer')
            return
          }

          // 正常更新權限和狀態
          console.log('更新登入狀態和權限...')
          localStorage.setItem('isLoggedIn', 'true')
          localStorage.setItem('loginTime', Date.now().toString())
          localStorage.setItem('userEmail', session.user.email)
          localStorage.setItem('accessToken', session.access_token)
          localStorage.setItem('userRole', role)

          setIsLoggedIn(true)
          setUserRole(role)

        } catch (error) {
          console.error('檢查用戶權限時發生錯誤，給予預設查看權限:', error)
          // 發生錯誤時給予查看權限而不是登出
          setIsLoggedIn(true)
          setUserRole('viewer')
          localStorage.setItem('userRole', 'viewer')
        }
      } else {
        console.log('沒有有效會話')
        // 沒有有效會話，檢查本地存儲
        const loginStatus = localStorage.getItem('isLoggedIn')
        const loginTime = localStorage.getItem('loginTime')

        if (loginStatus === 'true' && loginTime) {
          // 檢查登入是否過期（24小時）
          const now = Date.now()
          const loginTimeNum = parseInt(loginTime)
          const twentyFourHours = 24 * 60 * 60 * 1000

          if (now - loginTimeNum < twentyFourHours) {
            // 本地狀態有效但 Supabase 會話無效，需要重新登入
            clearLoginData()
            setIsLoggedIn(false)
          } else {
            // 登入已過期
            clearLoginData()
            setIsLoggedIn(false)
          }
        } else {
          setIsLoggedIn(false)
          setUserRole('viewer')
        }
      }
    } catch (error) {
      console.error('檢查登入狀態時發生嚴重錯誤:', error)
      clearLoginData()
      setIsLoggedIn(false)
    } finally {
      // 無論如何都要停止載入狀態
      console.log('登入狀態檢查完成，停止載入')
      setLoading(false)
    }
  }, [])

  // 清除登入資料
  const clearLoginData = () => {
    localStorage.removeItem('isLoggedIn')
    localStorage.removeItem('loginTime')
    localStorage.removeItem('userEmail')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('userRole')
  }

  // 檢查用戶權限
  const checkUserPermission = async (userEmail) => {
    try {
      if (!supabase || !userEmail) {
        return { role: 'viewer', isActive: true }
      }

      // 檢查是否為硬編碼的管理員
      const adminEmails = [
        process.env.REACT_APP_ADMIN_EMAIL || 'admin@example.com',
        'admin@well-management.com',
        'sht2313131@gmail.com'  // 您的管理員郵箱
      ]

      if (adminEmails.includes(userEmail.toLowerCase())) {
        return { role: 'admin', isActive: true }
      }

      // 對於非硬編碼管理員，嘗試從數據庫檢查權限（添加超時保護）
      try {
        // 創建查詢Promise
        const queryPromise = supabase
          .from('user_permissions')
          .select('role, is_active')
          .eq('email', userEmail.toLowerCase())
          .maybeSingle()

        // 創建超時Promise (3秒)
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('查詢超時')), 3000)
        })

        // 競速執行，任一完成就返回
        const { data, error } = await Promise.race([queryPromise, timeoutPromise])

        if (error) {
          console.warn('權限查詢失敗，使用預設權限:', error.message)
          return { role: 'viewer', isActive: true, isError: true }
        }

        if (!data) {
          // 沒有找到權限記錄，默認為 viewer
          return { role: 'viewer', isActive: true }
        }

        return { role: data.role || 'viewer', isActive: data.is_active !== false }
      } catch (dbError) {
        console.warn('權限查詢超時或失敗，使用預設權限:', dbError.message)
        return { role: 'viewer', isActive: true, isError: true }
      }
    } catch (error) {
      return { role: 'viewer', isActive: true, isError: true }
    }
  }

  const fetchWells = useCallback(async () => {
    try {
      setLoading(true)

      // 檢查配置
      const configStatus = checkSupabaseConfig()
      if (!configStatus.isValid) {
        setConfigError(configStatus)
        setWells([])
        setFilteredWells([])
        setError(null)
        setLoading(false)
        return
      }

      const data = await wellService.getAllWells()

      setWells(data || [])
      setFilteredWells(data || [])
      setError(null)
      setConfigError(null)

      // 顯示API key類型提示
      if (configStatus.keyType === 'anon') {
        console.warn('您正在使用舊的 anon key 格式。請考慮升級到新的 publishable key 格式。')
      }
    } catch (err) {
      console.error('載入水井資料失敗:', err)
      setError('無法連接到數據庫')
      setWells([])
      setFilteredWells([])
      setConfigError({
        error: `連接失敗: ${err.message || '未知錯誤'}`,
        suggestion: '請檢查您的 Supabase 配置、網絡連接，或嘗試清除瀏覽器快取 (F12 -> Application -> Clear site data)。'
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    // 恢復本地存儲的用戶角色
    const savedRole = localStorage.getItem('userRole')
    if (savedRole) {
      setUserRole(savedRole)
    }

    fetchWells()
    checkLoginStatus()

    // 監聽 Supabase 認證狀態變化
    let authListener = null
    if (supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        console.log('認證狀態變化:', event, session?.user?.email)

        if (event === 'SIGNED_OUT' || !session) {
          clearLoginData()
          setIsLoggedIn(false)
          setUserRole('viewer')
        } else if (event === 'SIGNED_IN' && session.user) {
          try {
            // 檢查用戶權限（非阻塞方式）
            const { role, isActive } = await checkUserPermission(session.user.email)

            if (isActive || role === 'admin') {
              setIsLoggedIn(true)
              setUserRole(role)
              localStorage.setItem('userRole', role)
            } else {
              // 帳號未啟用或待審核，但仍允許查看（設為 viewer）
              setIsLoggedIn(true)
              setUserRole('viewer')
              localStorage.setItem('userRole', 'viewer')

              if (role === 'pending') {
                console.warn('用戶帳號待審核，暫時給予查看權限')
              }
            }
          } catch (error) {
            console.error('檢查用戶權限失敗，給予預設查看權限:', error)
            // 權限檢查失敗時，給予預設查看權限而不是登出
            setIsLoggedIn(true)
            setUserRole('viewer')
            localStorage.setItem('userRole', 'viewer')
          }
        }
      })

      authListener = subscription
    }

    // 清理函數
    return () => {
      if (authListener) {
        authListener.unsubscribe()
      }
    }
  }, [fetchWells, checkLoginStatus])

  // 搜索和篩選功能
  useEffect(() => {
    let filtered = wells

    // 按指定欄位搜索：水井名稱、電號、站別、馬達類型
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(well => {
        try {
          // 1. 水井名稱
          const name = well.name ? String(well.name).toLowerCase() : ''
          // 2. 電號
          const eNum = well.e_num ? String(well.e_num).toLowerCase() : ''
          // 3. 站別
          const unit = well.unit ? String(well.unit).toLowerCase() : ''
          // 4. 馬達類型
          const motorType = well.motor_type ? String(well.motor_type).toLowerCase() : ''

          return name.includes(searchLower) ||
            eNum.includes(searchLower) ||
            unit.includes(searchLower) ||
            motorType.includes(searchLower)
        } catch (error) {
          console.error('搜尋過程中發生錯誤:', error, well)
          return false
        }
      })
    }


    // 按馬達類型篩選
    if (motorTypeFilter) {
      filtered = filtered.filter(well => well.motor_type === motorTypeFilter)
    }

    setFilteredWells(filtered)
  }, [wells, searchTerm, areaFilter, wellTypeFilter, motorTypeFilter])

  // 定期檢查權限變更（每3分鐘檢查一次）
  useEffect(() => {
    if (!isLoggedIn || !supabase) return

    const checkPermissionChanges = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()

        if (error || !session?.user) {
          return
        }

        const permissionResult = await checkUserPermission(session.user.email)

        // 如果查詢過程中發生錯誤或超時，則不進行權限變更通知，避免誤判
        if (permissionResult.isError) {
          return
        }

        const currentRole = permissionResult.role
        const storedRole = localStorage.getItem('userRole')

        if (storedRole && storedRole !== currentRole) {
          // 更新localStorage
          localStorage.setItem('userRole', currentRole)
          setUserRole(currentRole)

          // 顯示通知
          const roleLabels = {
            'admin': '管理員',
            'editor': '編輯者',
            'viewer': '查看者',
            'pending': '待審核'
          }

          alert(`權限變更通知\n\n您的權限已被管理員更改為：${roleLabels[currentRole] || currentRole}\n\n請重新整理頁面(F5)以應用新權限。`)
        }
      } catch (error) {
        // 靜默處理錯誤，不影響系統運作
      }
    }

    // 延遲10秒後才開始定期檢查，避免干擾頁面初始載入
    const initialTimeout = setTimeout(() => {
      checkPermissionChanges() // 立即檢查一次

      // 設置定期檢查（每3分鐘）
      const intervalId = setInterval(checkPermissionChanges, 3 * 60 * 1000)

      // 存儲interval ID以便清理
      window.permissionCheckInterval = intervalId
    }, 10000)

    return () => {
      clearTimeout(initialTimeout)
      if (window.permissionCheckInterval) {
        clearInterval(window.permissionCheckInterval)
        window.permissionCheckInterval = null
      }
    }
  }, [isLoggedIn])

  const handleDeleteWell = async (id) => {
    if (!requireAuth()) return

    if (configError) {
      alert('無法刪除：請檢查 Supabase 配置')
      return
    }

    if (window.confirm('確定要刪除這個水井資訊嗎？')) {
      try {
        await wellService.deleteWell(id)
        fetchWells() // 重新載入資料
      } catch (err) {
        setError('刪除水井資訊失敗')
        console.error('Error deleting well:', err)
      }
    }
  }

  const clearFilters = () => {
    setSearchTerm('')
    setAreaFilter('')
    setWellTypeFilter('')
    setMotorTypeFilter('')
  }

  const toggleCardExpansion = (wellId) => {
    setExpandedCards(prev => ({
      ...prev,
      [wellId]: !prev[wellId]
    }))
  }

  // 施工紀錄相關函數
  const handleOpenConstructionForm = () => {
    if (!requireAuth()) return
    setIsConstructionFormOpen(true)
  }

  const handleCloseConstructionForm = () => {
    setIsConstructionFormOpen(false)
  }

  const handleOpenConstructionList = () => {
    if (!requireAuth()) return
    setIsConstructionListOpen(true)
  }

  const handleCloseConstructionList = () => {
    setIsConstructionListOpen(false)
  }

  const handleConstructionRecordSuccess = (newRecord) => {
    console.log('施工紀錄新增成功:', newRecord)
    fetchWells()
  }

  // 新增水井相關函數
  const handleOpenWellForm = () => {
    if (!requireAuth()) return
    setIsWellFormOpen(true)
  }

  const handleCloseWellForm = () => {
    setIsWellFormOpen(false)
    setEditingWell(null)
  }

  const handleWellSuccess = (result) => {
    console.log(editingWell ? '水井更新成功:' : '水井新增成功:', result)
    fetchWells()
  }

  const handleEditWell = (well) => {
    if (!isLoggedIn) {
      setIsLoginFormOpen(true)
      return
    }
    setEditingWell(well)
    setIsWellFormOpen(true)
  }

  // 登入相關函數
  const handleLoginSuccess = async (user) => {
    try {
      const { role, isActive } = await checkUserPermission(user.email)

      if (!isActive && role !== 'admin') {
        alert('您的帳號尚未被啟用，暫時給予查看權限。如需編輯權限，請聯繫管理員')
        setIsLoggedIn(true)
        setUserRole('viewer')
        localStorage.setItem('userRole', 'viewer')
        return
      }

      if (role === 'pending') {
        alert('您的帳號正在審核中，暫時給予查看權限。請等待管理員核准以獲得編輯權限')
        setIsLoggedIn(true)
        setUserRole('viewer')
        localStorage.setItem('userRole', 'viewer')
        return
      }

      setIsLoggedIn(true)
      setUserRole(role)
      localStorage.setItem('userRole', role)
      checkLoginStatus()
    } catch (error) {
      console.error('處理登入權限檢查時發生錯誤，給予預設查看權限:', error)
      alert('權限檢查失敗，暫時給予查看權限')
      setIsLoggedIn(true)
      setUserRole('viewer')
      localStorage.setItem('userRole', 'viewer')
    }
  }

  const handleLogout = async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut()
      }
      clearLoginData()
      setIsLoggedIn(false)
      setUserRole('viewer')
      alert('已登出')
    } catch (error) {
      console.error('登出失敗:', error)
      clearLoginData()
      setIsLoggedIn(false)
      setUserRole('viewer')
      alert('已登出')
    }
  }

  const handleCloseLoginForm = () => {
    setIsLoginFormOpen(false)
  }

  // 需要認證的操作
  const requireAuth = (action) => {
    if (!isLoggedIn) {
      setIsLoginFormOpen(true)
      return false
    }
    return true
  }

  // 導航功能
  const handleNavigation = (well) => {
    const lat = well.lat || well.latitude
    const lng = well.lng || well.longitude

    if (!lat || !lng) {
      alert('此水井沒有經緯度資料，無法進行導航')
      return
    }
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    let mapUrl
    if (isMobile) {
      mapUrl = `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},17z`
    } else {
      mapUrl = `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},17z`
    }
    window.open(mapUrl, '_blank')
  }

  // 頁面導航功能
  const navigateToPage = (pageType) => {
    setShowFloatingMenu(false)

    if (pageType === 'pipe') {
      setCurrentPage('data-analysis')
    } else if (pageType === 'motor') {
      setCurrentPage('reports')
    } else if (pageType === 'wells') {
      setCurrentPage('wells')
    } else if (pageType === 'new-page') {
      setCurrentPage('new-page')
    } else if (pageType === 'wiring') {
      setCurrentPage('wiring-diagram')
    }
  }

  if (loading) return <div className="loading">載入中...</div>

  // 渲染水井內容
  const renderWellsContent = () => (
    <div className="well-list">
      <h2>水井資訊管理系統</h2>

      {/* 配置提示 */}
      {configError && (
        <div className="config-warning">
          <h3>配置錯誤</h3>
          <p><strong>錯誤：</strong>{configError.error}</p>
          <p><strong>建議：</strong>{configError.suggestion}</p>
        </div>
      )}

      {error && !configError && <div className="error">{error}</div>}

      {/* 搜索和篩選區域 */}
      <div className="search-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="搜索水井名稱、電號、站別或馬達類型..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-box">
          <select
            value={motorTypeFilter}
            onChange={(e) => setMotorTypeFilter(e.target.value)}
          >
            <option value="">所有馬達類型</option>
            {[...new Set(wells.map(well => well.motor_type).filter(Boolean))].map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <button className="clear-btn" onClick={clearFilters}>
          清除篩選
        </button>
      </div>

      {/* 結果統計和按鈕 */}
      <div className="results-header">
        <div className="results-info">
          <p>顯示 {filteredWells.length} 個結果 (共 {wells.length} 個水井)</p>
        </div>

        <div className="button-group">
          {isLoggedIn ? (
            <>
              {/* 施工紀錄按鈕 - 編輯者和管理員可用 */}
              <button
                className={`construction-btn ${(userRole === 'editor' || userRole === 'admin') ? '' : 'disabled'}`}
                onClick={handleOpenConstructionForm}
                disabled={userRole !== 'editor' && userRole !== 'admin'}
                title={userRole !== 'editor' && userRole !== 'admin' ? "需要編輯權限才能使用此功能" : "新增施工紀錄"}
              >
                新增施工紀錄(吊馬達)
              </button>

              <button
                className={`construction-btn ${(userRole === 'editor' || userRole === 'admin') ? '' : 'disabled'}`}
                onClick={handleOpenConstructionList}
                disabled={userRole !== 'editor' && userRole !== 'admin'}
                style={{ backgroundColor: '#f39c12', marginLeft: '10px' }}
                title={userRole !== 'editor' && userRole !== 'admin' ? "需要編輯權限才能使用此功能" : "查看施工紀錄"}
              >
                查看施工紀錄
              </button>

              {/* 新增水井按鈕 - 編輯者和管理員可用 */}
              <button
                className={`add-btn ${(userRole === 'editor' || userRole === 'admin') ? '' : 'disabled'}`}
                onClick={handleOpenWellForm}
                disabled={userRole !== 'editor' && userRole !== 'admin'}
                title={userRole !== 'editor' && userRole !== 'admin' ? "需要編輯權限才能使用此功能" : "新增水井資訊"}
              >
                新增水井
              </button>

              {/* 用戶管理按鈕 - 僅管理員可見 */}
              {userRole === 'admin' && (
                <button
                  className="user-management-btn"
                  onClick={() => setIsUserManagementOpen(true)}
                  style={{
                    background: '#17a2b8',
                    color: 'white',
                    border: 'none',
                    padding: '10px 16px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                  title="管理用戶權限"
                >
                  用戶管理
                </button>
              )}

              <button
                className="logout-btn"
                onClick={handleLogout}
              >
                登出 ({userRole === 'admin' ? '管理員' : userRole === 'editor' ? '編輯者' : '查看者'})
              </button>
            </>
          ) : (
            <>
              <button
                className="construction-btn disabled"
                onClick={handleOpenConstructionForm}
                title="需要登入才能使用此功能"
              >
                新增施工紀錄
              </button>
              <button
                className="construction-btn disabled"
                onClick={handleOpenConstructionList}
                style={{ backgroundColor: '#f39c12', marginLeft: '10px' }}
                title="需要登入才能使用此功能"
              >
                查看施工紀錄
              </button>
              <button
                className="add-btn disabled"
                onClick={handleOpenWellForm}
                title="需要登入才能使用此功能"
              >
                + 新增水井
              </button>
              <button
                className="login-btn"
                onClick={() => setIsLoginFormOpen(true)}
              >
                登入
              </button>
              <button
                className="register-btn"
                onClick={() => setIsRegisterFormOpen(true)}
                style={{
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                註冊
              </button>
            </>
          )}
        </div>
      </div>

      {filteredWells.length === 0 ? (
        <div className="no-data">
          {wells.length === 0 ? (
            <div>
              <p>目前沒有水井資訊</p>
              <p>可能的原因：</p>
              <ul>
                <li>數據庫中沒有資料</li>
                <li>RLS 政策阻止了資料讀取</li>
                <li>連接問題</li>
              </ul>
            </div>
          ) : (
            <div>
              <p>沒有符合條件的水井資訊</p>
              <p>原始資料數量：{wells.length}</p>
              <p>篩選後數量：{filteredWells.length}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="wells-grid">
          {filteredWells.map((well, index) => {
            const wellId = well.id || well.e_num || index + 1
            const wellName = well.name || (well.e_num ? `${well.e_num}號井` : `第${index + 1}號井`)
            const isExpanded = expandedCards[wellId]

            return (
              <div key={`well-${index}-${wellId}`} className="well-card">
                <div className="well-header">
                  <h3>{wellName}{well.unit ? ' ' + well.unit : ''}</h3>

                  <div className="header-buttons">
                    <button
                      className="nav-btn"
                      onClick={() => handleNavigation(well)}
                      title="開啟導航"
                    >
                      導航
                    </button>
                    <button
                      className="expand-btn"
                      onClick={() => toggleCardExpansion(wellId)}
                    >
                      {isExpanded ? '收起' : '展開'}
                    </button>
                  </div>
                </div>

                <div className="well-info">
                  {/* 基本顯示 */}
                  <h4>廠商吊井資料</h4>
                  <div className="basic-info">
                    <div className="info-row">
                      <span className="info-label">馬力：</span>
                      <span className="info-value">{well.hp || '未知'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">出水管徑：</span>
                      <span className="info-value">{well['outlet_pipe_diameter(inch)'] ? `${well['outlet_pipe_diameter(inch)']}` : '無資料'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">出水管材質&多長、幾隻：</span>
                      <span className="info-value">
                        {well.outlet_pipe_material || '無資料'}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">馬達安裝深度：</span>
                      <span className="info-value">{well.pump_depth ? `${well.pump_depth}` : '無資料'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="expanded-info">
                      <div className="info-section">
                        <div className="info-row">
                          <span className="info-label">馬達類型：</span>
                          <span className="info-value">{well.motor_type || '未知'}</span>
                        </div>

                        <div className="info-row">
                          <span className="info-label">水位觀測管安裝深度：</span>
                          <span className="info-value">
                            {well['installation_depth_of_water_level_observation_pipe(m)'] ? `${well['installation_depth_of_water_level_observation_pipe(m)']}` : '無資料'}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">靜水位：</span>
                          <span className="info-value">
                            {well['static_water_level(m)'] ? `${well['static_water_level(m)']}` : '無資料'}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">故障原因：</span>
                          <span className="info-value">
                            {well.failure_reason || '無資料'}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">修復日期：</span>
                          <span className="info-value">
                            {well.repair_date || '無資料'}
                          </span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">廠商名：</span>
                          <span className="info-value">
                            {well.vendor || '無資料'}
                          </span>
                        </div>
                      </div>

                      <div className="info-section">
                        <h4>重要資訊</h4>
                        <div className="info-row">
                          <span className="info-label">電號：</span>
                          <span className="info-value">{well.e_num || '未知'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">水權狀號：</span>
                          <span className="info-value">{well.water_certificate || '無'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">水井代碼：</span>
                          <span className="info-value">{well.well_key || '無'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">契約容量：</span>
                          <span className="info-value">{well.kw || '無'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">地號：</span>
                          <span className="info-value">{well.place_number || '無'}</span>
                        </div>
                      </div>

                      <div className="info-section">
                        <h4>井體規格</h4>
                        <div className="info-row">
                          <span className="info-label">井深：</span>
                          <span className="info-value">{well.well_deep_m ? `${well.well_deep_m} 米` : '未知'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">井徑：</span>
                          <span className="info-value">{well.well_diameter_inch ? `${well.well_diameter_inch} 英寸` : '未知'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">井管材質：</span>
                          <span className="info-value">{well.well_tube_material || '未知'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">井類型：</span>
                          <span className="info-value">{well['30m_up_or_down'] || '未知'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">井體設置日期：</span>
                          <span className="info-value">{well.well_setting_date || '未知'}</span>
                        </div>
                      </div>

                      <div className="info-section">
                        <h4>其他資訊</h4>
                        <div className="info-row">
                          <span className="info-label">備註1：</span>
                          <span className="info-value">{well.note || '無'}</span>
                        </div>
                        <div className="info-row">
                          <span className="info-label">備註2：</span>
                          <span className="info-value">{well.note2 || '無'}</span>
                        </div>
                      </div>

                    </div>
                  )}
                </div>


                <div className="well-actions">
                  {(userRole === 'admin' || userRole === 'editor') && (
                    <button
                      className="edit-btn"
                      onClick={() => handleEditWell(well)}
                      title="編輯水井資訊"
                    >
                      編輯
                    </button>
                  )}
                  {userRole === 'admin' && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteWell(well.id)}
                      title="刪除水井資訊"
                    >
                      刪除
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* 施工紀錄表單 Modal */}
      {isConstructionFormOpen && (
        <ConstructionRecordForm
          isOpen={true}
          onClose={handleCloseConstructionForm}
          onSuccess={handleConstructionRecordSuccess}
        />
      )}

      {/* 施工紀錄列表 Modal */}
      {isConstructionListOpen && (
        <ConstructionRecordList
          isOpen={true}
          onClose={handleCloseConstructionList}
        />
      )}

      {/* 新增/編輯水井表單 Modal */}
      {isWellFormOpen && (
        <WellForm
          isOpen={true}
          onClose={handleCloseWellForm}
          onSuccess={handleWellSuccess}
          editData={editingWell}
        />
      )}

      {/* 登入 Modal */}
      {isLoginFormOpen && (
        <LoginForm
          isOpen={true}
          onClose={handleCloseLoginForm}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* 註冊 Modal */}
      {isRegisterFormOpen && (
        <RegisterForm
          isOpen={true}
          onClose={() => setIsRegisterFormOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterFormOpen(false)
            setIsLoginFormOpen(true)
            alert('註冊成功，請登入。您的帳號需要管理員核准後才能獲得編輯權限。')
          }}
        />
      )}

      {/* 用戶管理 Modal */}
      {isUserManagementOpen && (
        <UserManagement
          isOpen={true}
          onClose={() => setIsUserManagementOpen(false)}
          userRole={userRole}
        />
      )}
    </div>
  )

  // 根據當前頁面渲染內容
  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'data-analysis':
        return <PvcPipesQuery />
      case 'reports':
        return <MotorQuery />
      case 'new-page':
        return <NewPage />
      case 'wiring-diagram':
        return <WiringDiagram />
      case 'wells':
      default:
        return renderWellsContent()
    }
  }

  return (
    <>
      {renderCurrentPage()}

      {/* 懸浮導航按鈕 (僅在登入且非 pending 狀態顯示) */}
      {isLoggedIn && userRole !== 'pending' && (
        <div className="floating-nav">
          <button
            className={`floating-btn ${showFloatingMenu ? 'active' : ''}`}
            onClick={() => setShowFloatingMenu(!showFloatingMenu)}
            title="快速導航"
          >
            <div className="hamburger-menu">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>

          {showFloatingMenu && (
            <div className="floating-menu">
              {currentPage !== 'wells' && (
                <button
                  className="menu-item"
                  onClick={() => navigateToPage('wells')}
                >
                  回到首頁
                </button>
              )}
              <button
                className="menu-item"
                onClick={() => navigateToPage('pipe')}
              >
                管徑查詢
              </button>
              <button
                className="menu-item"
                onClick={() => navigateToPage('motor')}
              >
                馬達相關查詢
              </button>
              <button
                className="menu-item"
                onClick={() => navigateToPage('wiring')}
              >
                接線圖
              </button>
              <button
                className="menu-item"
                onClick={() => navigateToPage('new-page')}
              >
                功能頁面
              </button>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default WellList